import PageHero from "@/components/PageHero";
import { StaggerChildren, StaggerItem } from "@/components/Motion";
import { Quote } from "lucide-react";

import SeoHead from "@/components/SeoHead";
import { SectionLoading, SectionError, SectionEmpty } from "@/components/RichContent";
import { useTestimonials } from "@/hooks/usePublicData";

export default function Testimonials() {
  const { data, isLoading, isError, refetch } = useTestimonials();
  const items = (data && data.items) || [];

  return (
    <>
      <SeoHead pageKey="testimonials" />

      <PageHero
        eyebrow="Testimonials"
        chapter="Chapter — Voices"
        lines={["In our clients' words.", "Anonymised, unedited."]}
        blurb="Clients are identified only by engagement type. No photographs are used. Selected excerpts have been lightly edited for length; the meaning is preserved."
        image="/assets/hero-testimonials"
        imageAlt="A client testimonial displayed on a desk stand reading: their advice was independent, thorough and genuinely in our best interest"
      />

      <section className="bg-[#060f1e] py-16 lg:py-24">
        {isLoading ? (
          <SectionLoading label="Loading testimonials" />
        ) : isError ? (
          <SectionError onRetry={refetch} />
        ) : (
          <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
            <StaggerChildren className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {items.length === 0 ? (
                <SectionEmpty
                  title="No testimonials published yet"
                  description="Client feedback is published only once the client has approved it."
                />
              ) : (
                items.map((t, i) => (
                  <StaggerItem key={t.id}>
                    <div className="relative bg-[#0c1728] border border-[#1a2940] p-10 lg:p-12 h-full hover:border-[#cca24d]/50 transition-colors duration-700 group">
                      <Quote
                        className="absolute top-8 right-8 h-8 w-8 text-[#cca24d]/25 group-hover:text-[#cca24d]/50 transition-colors"
                        strokeWidth={1}
                      />
                      <div className="text-xs text-[#cca24d]/60 tnum mb-8">
                        {String(i + 1).padStart(2, "0")}
                      </div>
                      <p className="font-serif text-2xl md:text-3xl text-[#f7f5f0] font-light leading-snug">
                        &ldquo;{t.content}&rdquo;
                      </p>
                      <div className="mt-10 pt-6 border-t border-[#1a2940] flex items-baseline justify-between gap-4">
                        <div>
                          <div className="text-sm text-[#f7f5f0]">
                            {t.clientLabel || "PIRAC client"}
                            {t.clientInitials && (
                              <span className="text-[#9ca3af]"> · {t.clientInitials}</span>
                            )}
                          </div>
                          {t.engagement && (
                            <div className="text-xs text-[#9ca3af] tracking-wider uppercase mt-1">
                              {t.engagement}
                            </div>
                          )}
                          {t.companyName && (
                            <div className="text-xs text-[#6b7280] mt-1">{t.companyName}</div>
                          )}
                        </div>
                        {t.rating && (
                          <div
                            className="text-xs text-[#cca24d] tnum shrink-0"
                            aria-label={`Rated ${t.rating} out of 5`}
                          >
                            {"★".repeat(t.rating)}
                            <span className="text-[#1a2940]">{"★".repeat(5 - t.rating)}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </StaggerItem>
                ))
              )}
            </StaggerChildren>
          </div>
        )}
      </section>
    </>
  );
}
